<template>
    <div
        class="bg-light d-flex justify-content-center vh-100"
        v-if="!registered"
    >
        <div class="d-flex flex-column align-items-center align-self-center">
            <vue-qrcode
                class="border border-2 border-dark p-2"
                :value="qrcodevalue"
                :options="{ width: 300 }"
            ></vue-qrcode>
            <span class="text-dark display-3"
                ><a
                    :href="qrcodevalue"
                    target="_new"
                    >{{ randomcode }}</a
                ></span
            >
        </div>
    </div>
    <ProductionClientDisplayView
        v-else-if="settings !== null"
        :setting="settings"
    />
    <div
        v-else
        class="p-5"
    >
        <AlertMessage
            type="info"
            class="vw-100"
            ><span v-html="$tm('production.unknowndeviceevent', '', true)"></span
        ></AlertMessage>
    </div>
</template>
<script lang="ts" setup>
/**
 * This is the view that should be opened on the TV or big screen
 *
 * For testing purposes you can open this view in a browser and it will generate a QR code.
 *
 * NOTE! You will need to change the productionscreen_url in appsetting.ts to the correct url for your production screen.
 * Should be '//managerdummy.eqtiming.com/production/registerdevice'
 *
 * Open the address https://managerdummy.eqtiming.com/?production=true to get the QR code.
 * 1. Then click on the QR code to open the login page.
 *
 * @see https://www.npmjs.com/package/@types/qrcodejs
 * @see https://github.com/fengyuanchen/vue-qrcode
 */
import { ProductionDao } from '@/dao';
import type { ProductionDeviceSetting } from '@/objects/generated';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { randomstring } from '@/utilities/utils';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import { onMounted, ref } from 'vue';
import ProductionClientDisplayView from './ProductionClientDisplayView.vue';
import { AlertMessage } from '@/components';
import { GetAppsetting } from '@/composeables';

// This need to change to the correct url
const registered = ref<boolean>(document.location.href.includes('code='));

const productionscreen_url = GetAppsetting('productionscreen_url');

const registeredCode = ref<string | null>(new URLSearchParams(document.location.search).get('code'));

// The currentsettings for the screen
const settings = ref<ProductionDeviceSetting | null>(null);

// Generate a random code that we use to identify the screen
const randomcode = ref<string>(registeredCode.value ?? randomstring('').toUpperCase());

// The user agent of the screen
const userAgent = !navigator || !navigator.userAgent ? '' : btoa(navigator.userAgent);

// The QR code value wich consists of the url and the random code
// This will redirect your phone to a loginpage for admin, that will connect your user with the screen
const qrcodevalue = ref<string>(`${document.location.protocol}${productionscreen_url}/${randomcode.value}?ua=${userAgent}`);

// Set up a keepalive poller
const keepAliveInterval = 30000;

/**
 * Send a keepalive to the server to let it know that the screen is still alive
 */
window.setInterval(() => {
    runKeepalive();
}, keepAliveInterval);

/**
 * We tell the server the device is still alive
 */
const runKeepalive = () => {
    // If the setting is not set, we do not need to send a keepalive then it will be marked as offline in the backend after a while
    if (!settings.value || registered.value === false) {
        return;
    }

    ProductionDao.ProductionDeviceIsAlive(settings.value, settings.value?.EventId ?? 0);
};

onMounted(() => {
    // const clientScreen = useClientScreen();

    // Set up a signalR connection to the server with the random code
    // NOTE! Remember that this screen is on a random TV or big screen somewhere and is not the same instance as the controller
    //        That is why we set up this communication to the controller. This is where we will receive all the settings for the view from manager
    signalrSubscribe.ProductionDeviceSetting({ DeviceID: randomcode.value }, (data: ProductionDeviceSetting[]) => {
        console.log('ProductionScreen', data);

        if (!data || data.length === 0) {
            return;
        }

        if (registered.value && data[0].Tables.length === 0) {
            registered.value = false;
            settings.value = null;
            randomcode.value = randomstring('').toUpperCase();
        } else {
            registered.value = true;
            settings.value = data[0];
        }

        runKeepalive();
    });
});
</script>
